import React, { CSSProperties, ReactNode } from "react";
import { PlacedFirefly } from "./PlacedFirefly";
import { getHeaderExcursion } from "./headerExcursion";
import {
  createSceneTransform,
  createUnconstrainedSceneTransform,
} from "./geometry";
import {
  HeaderViewportMode,
  PetHeaderComposition,
  PetHeaderFireflyPlacement,
  PetHeaderSticker,
  SceneSize,
} from "./types";
import cls from "./styles.module.scss";
import { PlantArtwork } from "./PlantArtwork";

interface PetHeaderRendererProps {
  /** Static homepage demo uses 2D; pet/editor callers retain existing hints. */
  transformMode?: "2d" | "3d";
  plantDraggingId?: string | null;
  interactivePlants?: boolean;
  idleFireflies?: boolean;
  highlightedLayer?: string | null;
  allowCropOverflow?: boolean;
  composition: PetHeaderComposition;
  viewportMode: HeaderViewportMode;
  viewportSize: SceneSize;
}

const byOrder = <T extends { order: number }>(left: T, right: T) =>
  left.order - right.order;

const renderSticker = (
  sticker: PetHeaderSticker,
  canvasWidth: number,
  canvasHeight: number,
  plantDraggingId: string | null,
  interactivePlants: boolean
) =>
  sticker.plant ? (
    <PlantArtwork
      key={sticker.id}
      sticker={sticker}
      canvasWidth={canvasWidth}
      canvasHeight={canvasHeight}
      ghost={plantDraggingId === sticker.id}
      interactive={interactivePlants}
    />
  ) : (
    <img
      alt=""
      aria-hidden="true"
      className={cls.sticker}
      data-sticker-key={sticker.asset.key}
      draggable={false}
      key={sticker.id}
      src={sticker.asset.imageUrl}
      style={{
        left: sticker.x * canvasWidth,
        top: sticker.y * canvasHeight,
        width: sticker.width * canvasWidth,
        zIndex: sticker.stackOrder,
      }}
    />
  );

export function PetHeaderRenderer({
  transformMode = "3d",
  plantDraggingId = null,
  interactivePlants = true,
  idleFireflies = false,
  highlightedLayer,
  allowCropOverflow = false,
  composition,
  viewportMode,
  viewportSize,
}: PetHeaderRendererProps) {
  const { header } = composition;
  const crop =
    viewportMode === "mobile"
      ? composition.mobileCrop
      : composition.desktopCrop;
  const transform = (allowCropOverflow
    ? createUnconstrainedSceneTransform
    : createSceneTransform)(
    crop,
    { width: header.canvasWidth, height: header.canvasHeight },
    viewportSize
  );
  const sceneStyle: CSSProperties = {
    width: header.canvasWidth,
    height: header.canvasHeight,
    transform:
      transformMode === "2d"
        ? `translate(${transform.offsetX}px, ${transform.offsetY}px) scale(${transform.scale})`
        : `translate3d(${transform.offsetX}px, ${transform.offsetY}px, 0) scale(${transform.scale})`,
    // Override the shared CSS hint only for the static 2D demo. This leaves
    // the browser free to choose its layers instead of requesting one upfront.
    willChange: transformMode === "2d" ? "auto" : undefined,
  };

  const backgrounds = header.layers
    .filter((layer) => layer.kind === "background")
    .sort(byOrder);
  const overlays = header.layers
    .filter((layer) => layer.kind === "overlay")
    .sort(byOrder);
  const depthSlots = [...header.depthSlots].sort(byOrder);
  const stickersByDepth = new Map<string, PetHeaderSticker[]>();
  const firefliesByDepth = new Map<string, PetHeaderFireflyPlacement[]>();
  composition.stickers.forEach((sticker) => {
    const entries = stickersByDepth.get(sticker.depthSlot) ?? [];
    entries.push(sticker);
    stickersByDepth.set(sticker.depthSlot, entries);
  });
  stickersByDepth.forEach((stickers) =>
    stickers.sort((left, right) => left.stackOrder - right.stackOrder)
  );
  composition.fireflies
    .filter((firefly) => firefly.isLit)
    .forEach((firefly) => {
      const entries = firefliesByDepth.get(firefly.depthSlot) ?? [];
      entries.push(firefly);
      firefliesByDepth.set(firefly.depthSlot, entries);
    });

  const stack: ReactNode[] = backgrounds.map((layer) => (
    <img
      alt=""
      aria-hidden="true"
      className={cls.artLayer}
      data-stack-item={`layer:${layer.key}`}
      draggable={false}
      key={`layer:${layer.key}`}
      src={layer.imageUrl}
    />
  ));

  depthSlots.forEach((slot, index) => {
    stack.push(
      <div
        className={cls.stickerLayer}
        data-stack-item={`depth:${slot.key}`}
        key={`depth:${slot.key}`}
      >
        {[
          ...(stickersByDepth.get(slot.key) ?? []).map((sticker) => ({
            id: sticker.id,
            kind: "sticker" as const,
            stackOrder: sticker.stackOrder,
            value: sticker,
          })),
          ...(firefliesByDepth.get(slot.key) ?? []).map((firefly) => ({
            id: firefly.id,
            kind: "firefly" as const,
            stackOrder: firefly.stackOrder,
            value: firefly,
          })),
        ]
          .sort(
            (left, right) =>
              left.stackOrder - right.stackOrder ||
              left.id.localeCompare(right.id)
          )
          .map((item) =>
            item.kind === "sticker" ? (
              renderSticker(
                item.value,
                header.canvasWidth,
                header.canvasHeight,
                plantDraggingId,
                interactivePlants
              )
            ) : (
              <PlacedFirefly
                key={item.value.id}
                firefly={item.value}
                canvasWidth={header.canvasWidth}
                canvasHeight={header.canvasHeight}
                idle={idleFireflies}
                excursion={
                  idleFireflies
                    ? getHeaderExcursion(
                        item.value,
                        composition.stickers,
                        {
                          width: header.canvasWidth,
                          height: header.canvasHeight,
                        },
                        viewportSize,
                        transform
                      )
                    : null
                }
              />
            )
          )}
      </div>
    );
    const overlay = overlays[index];
    if (overlay) {
      stack.push(
        <img
          alt=""
          aria-hidden="true"
          className={`${cls.artLayer} ${
            highlightedLayer === overlay.key ? cls.highlightedLayer : ""
          }`}
          data-stack-item={`layer:${overlay.key}`}
          draggable={false}
          key={`layer:${overlay.key}`}
          src={overlay.imageUrl}
        />
      );
    }
  });

  overlays.slice(depthSlots.length).forEach((layer) => {
    stack.push(
      <img
        alt=""
        aria-hidden="true"
        className={cls.artLayer}
        data-stack-item={`layer:${layer.key}`}
        draggable={false}
        key={`layer:${layer.key}`}
        src={layer.imageUrl}
      />
    );
  });

  return (
    <div className={cls.viewport} data-testid="pet-header-renderer">
      <div className={cls.scene} style={sceneStyle}>
        {stack}
      </div>
    </div>
  );
}
